import { QueryClient } from '@tanstack/react-query';
import { retryDelay, shouldRetry } from './components/states/fault.ts';

/* ==========================================================================
   The one QueryClient.

   Every state an operator sees — reading, held, no reply, gone — is produced
   by this policy. The panel chrome only reports it. So the two have to agree,
   and the retry schedule the fault line counts down to is literally the
   function this file installs (see components/states/fault.ts).

   Four decisions, each of which changes what is on screen:
   ========================================================================== */

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /* 1 · RETRY WHAT A RETRY COULD FIX.
           The old default was `retry: 1` — one retry for everything, which
           means a 404 on a deregistered robot takes two round trips to admit
           it is a 404, and a dropped site link gives up after one. The policy
           is per class now: transport 3, 502/503/504 3, 500 1, 429 2,
           401/403/404/422 none. */
        retry: shouldRetry,
        retryDelay,

        /* 2 · NEVER PAUSE A READ BECAUSE THE BROWSER THINKS IT IS OFFLINE.
           TanStack's default `networkMode: 'online'` parks a query in
           `fetchStatus: 'paused'` when `navigator.onLine` is false, with no
           error and no data — which the console can only render as a spinner
           that never resolves. That flag is also wrong for us in both
           directions: a laptop cabled to an isolated robot network reports
           offline while the API is a metre away, and a laptop on café wi-fi
           reports online while the VPN to the API is down.

           So: always attempt, and report what the attempt actually did. Every
           "no reply" in this console is a request that was really made. */
        networkMode: 'always',

        /* 3 · THE MOMENT THE OPERATOR IS BACK, EVERYTHING RE-READS.
           `'always'` rather than `true` on both, so a source that is still
           inside its staleTime is re-read too: after an outage — or after ten
           minutes in another window — the operator's question is "what did I
           miss", and answering it with a cached copy is the wrong answer by
           definition.

           Focus matters here precisely BECAUSE every surface polls. A poll
           timer does not run while the tab is hidden (see 3b), so a console
           left on a second monitor and come back to would otherwise sit on a
           figure up to a full interval old, wearing a `Polling 5s` tag. One
           read on focus closes that window to nothing.

           It is not free: the fleet register mounts one catalog read per
           morphology and one telemetry read per selected robot, so an alt-tab
           back into /fleet/list is tens of requests, not four. That is the
           right trade against a screen an operator is about to act on — but it
           is a trade, and it is why 3b exists to stop the same reads running
           while nobody is looking. */
        refetchOnReconnect: 'always',
        refetchOnWindowFocus: 'always',

        /* 3b · A HIDDEN TAB DOES NOT POLL.
           TanStack's default, stated because it is a decision: a console left
           open for a week should not hold a 5 s request loop against the API
           from a tab nobody is looking at. Nothing is lost — the figure comes
           back on focus (above), and it comes back with its true age beside
           it either way, which is the only thing that would make silence
           dangerous. */
        refetchIntervalInBackground: false,

        /* 4 · A SHORT STALE WINDOW, BECAUSE THE FLEET MOVES.
           5 s matches the fastest poll on the fleet surfaces: long enough that
           navigating between routes does not re-fetch the same list three
           times, short enough that a remounted panel never shows a figure the
           operator has already seen change somewhere else. */
        staleTime: 5_000,

        /* 5 · A CACHE THAT OUTLIVES AN OUTAGE.
           TanStack drops an unobserved query's data after 5 minutes. That is
           the wrong number for a console whose whole failure design is "keep
           the figures and mark them as not current": an operator who leaves
           /fleet for the command queue during an eight-minute API outage and
           comes back gets a COLD START — the last known fleet, which was
           still on screen when they navigated, has been garbage-collected
           into a first read that then fails. 30 minutes keeps the last-known
           picture available for as long as an incident realistically runs,
           and it is safe precisely because nothing in this console shows a
           cached figure without its age beside it.

           It costs a few hundred KB of JSON. */
        gcTime: 30 * 60_000,

        /* Referential identity across polls, so a 5 s refetch that returns the
           same 28 robots re-renders nothing. A number that repaints when it
           has not changed is a number an operator learns to distrust. */
        structuralSharing: true,

        /* 6 · A FAILED READ NEVER THROWS INTO THE TREE.
           `throwOnError: true` would send a fault to the nearest error
           boundary, which is a route-scale answer to a panel-scale event: one
           dead endpoint would unmount the surface the operator is working on.
           Faults are values here, rendered in place by the panel that owns the
           read (components/states). Explicit rather than relied upon, because
           it is the single decision that keeps the failure design possible. */
        throwOnError: false,
      },

      mutations: {
        /* A command dispatch is not idempotent. `stop_mapping` sent twice
           because the first reply was lost is two entries in the robot's
           queue, and the console cannot tell a lost reply from a lost
           request. Writes are retried by a human who can see the queue, or
           not at all. */
        retry: 0,
        networkMode: 'always',
      },
    },
  });
}
